import { useEffect, type RefObject } from 'react';

// iOS touch guard for overlays (Round 2 §X, belt and braces to the portal + body lock).
//
// WKWebView still lets a touch scroll chain out of an element that cannot scroll in the gesture's
// direction. On an overlay backdrop the only element allowed to scroll is the overlay's own scroll
// container (`[data-scroll-container]`, i.e. `.sheet`), and only while it actually has room in that
// direction; a horizontal strip inside the sheet (`[data-scroll-x]`) may scroll sideways. Every
// other touch move on the backdrop is cancelled, so nothing behind the overlay can ever receive it.
// Listeners are non-passive on purpose (preventDefault must be honoured).
function nearest(el: Element | null, selector: string, within: HTMLElement): HTMLElement | null {
  let cursor: Element | null = el;
  while (cursor && cursor !== within.parentElement) {
    if (cursor instanceof HTMLElement && cursor.matches(selector)) return cursor;
    cursor = cursor.parentElement;
  }
  return null;
}

export function shouldCancelOverlayTouch(input: { target: Element | null; backdrop: HTMLElement; dx: number; dy: number }): boolean {
  const { target, backdrop, dx, dy } = input;
  const horizontal = Math.abs(dx) > Math.abs(dy);
  if (horizontal) {
    const strip = nearest(target, '[data-scroll-x]', backdrop);
    if (strip && backdrop.contains(strip) && strip.scrollWidth > strip.clientWidth) return false;
    return true;
  }
  const scroller = nearest(target, '[data-scroll-container]', backdrop);
  if (!scroller || !backdrop.contains(scroller)) return true;
  const room = scroller.scrollHeight - scroller.clientHeight;
  if (room <= 1) return true;
  if (dy > 0 && scroller.scrollTop <= 0) return true; // pulling down at the top
  if (dy < 0 && scroller.scrollTop >= room - 1) return true; // pushing up at the bottom
  return false;
}

export function useOverlayTouchGuard(ref: RefObject<HTMLElement | null>, active: boolean = true): void {
  useEffect(() => {
    const el = ref.current;
    if (!active || !el) return undefined;
    let startX = 0;
    let startY = 0;
    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch) {
        startX = touch.clientX;
        startY = touch.clientY;
      }
    };
    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch || event.touches.length > 1) return;
      const cancel = shouldCancelOverlayTouch({ target: event.target as Element | null, backdrop: el, dx: touch.clientX - startX, dy: touch.clientY - startY });
      if (cancel && event.cancelable) event.preventDefault();
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
    };
  }, [ref, active]);
}
